import { useState, useRef } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { listDriveContents, createFolder, uploadFile, type DriveItem } from "@/lib/api/knowledge-base"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FileBrowser } from "@/components/file-browser/FileBrowser"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { FolderPlus, Loader2, Upload, Image, FileText, FileSpreadsheet } from "lucide-react"
import { format } from "date-fns"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

type FileType = 'all' | 'images' | 'documents' | 'pdfs'

const FILE_TYPE_EXTENSIONS = {
  images: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  documents: ['.doc', '.docx', '.txt', '.rtf'],
  pdfs: ['.pdf']
}

const ALLOWED_EXTENSIONS = Object.values(FILE_TYPE_EXTENSIONS).flat()

export const MyDrive = ({ profile, session }: { profile: any | null, session: any | null }) => {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [currentPath, setCurrentPath] = useState("")
  const [search, setSearch] = useState("")
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'updated_at' | 'size'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [newFolderName, setNewFolderName] = useState("")
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false)
  const [selectedItem, setSelectedItem] = useState<DriveItem | null>(null)
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)
  const [fileType, setFileType] = useState<FileType>('all')
  const [showButtons, setShowButtons] = useState(false)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['drive-contents', currentPath, search, sortBy, sortOrder, fileType],
    queryFn: () => listDriveContents(session?.access_token || '', {
      path: currentPath,
      search,
      sortBy,
      sortOrder,
      limit: 50,
      fileType: fileType === 'all' ? undefined : fileType
    }),
    enabled: !!session?.access_token
  })

  const { data: itemDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: ['drive-item', selectedItem?.path],
    queryFn: () => listDriveContents(session?.access_token || '', {
      path: selectedItem?.path || ''
    }),
    enabled: !!session?.access_token && !!selectedItem?.path && showDetailsDialog
  })

  const uploadFileMutation = useMutation({
    mutationFn: (file: File) => uploadFile(session?.access_token || '', file, currentPath),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drive-contents'] })
      setUploadProgress(null)
      toast.success("File uploaded successfully")
    },
    onError: (error: any) => {
      setUploadProgress(null)
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to upload file"
      toast.error(errorMessage)
      if (error?.response?.status === 413) {
        toast.error("File size exceeds server limit")
      } else if (error?.response?.status === 415) {
        toast.error("File type not supported")
      } else if (error?.response?.status === 401) {
        toast.error("Please log in again to upload files")
      } else if (error?.response?.status === 403) {
        toast.error("You don't have permission to upload files")
      }
      console.error("Failed to upload file:", error)
    }
  })

  const createFolderMutation = useMutation({
    mutationFn: () => createFolder(session?.access_token || '', currentPath, newFolderName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drive-contents'] })
      setShowNewFolderDialog(false)
      setNewFolderName("")
      toast.success("Folder created successfully")
    },
    onError: (error) => {
      toast.error("Failed to create folder")
      console.error("Failed to create folder:", error)
    }
  })

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      toast.error(`Unsupported file format. Allowed formats: ${ALLOWED_EXTENSIONS.join(', ')}`)
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 5MB")
      return
    }

    setUploadProgress("Uploading file...")
    uploadFileMutation.mutate(file)
  }

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      toast.error("Please enter a folder name")
      return
    }
    createFolderMutation.mutate()
  }

  const handleItemClick = (item: DriveItem) => {
    if (!item.size) { // It's a folder
      setCurrentPath(item.path)
    } else {
      setSelectedItem(item)
      setShowDetailsDialog(true)
    }
  }

  const handleMove = (item: DriveItem) => {
    // TODO: Implement move functionality
    console.log('Move:', item)
  }

  const handleGetDetails = (item: DriveItem) => {
    setSelectedItem(item)
    setShowDetailsDialog(true)
  }

  const formatSize = (size?: number) => {
    if (!size) return '-'
    const units = ['B', 'KB', 'MB', 'GB']
    let value = size
    let unitIndex = 0
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024
      unitIndex++
    }
    return `${value.toFixed(1)} ${units[unitIndex]}`
  }

  const filteredData = data?.data?.items?.filter(item => {
    if (fileType === 'all' || !item.size) return true // Show all items or folders
    const extension = item.name.toLowerCase().slice(item.name.lastIndexOf('.'))
    return FILE_TYPE_EXTENSIONS[fileType]?.includes(extension)
  }) || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">My Drive</h1>
          {isFetching && !isLoading && (
            <div className="animate-spin">
              <Loader2 className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Select value={fileType} onValueChange={(value: FileType) => setFileType(value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>All Files</span>
                </div>
              </SelectItem>
              <SelectItem value="images">
                <div className="flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  <span>Images</span>
                </div>
              </SelectItem>
              <SelectItem value="documents">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>Documents</span>
                </div>
              </SelectItem>
              <SelectItem value="pdfs">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  <span>PDFs</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <input
              type="file"
              className="hidden"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept={ALLOWED_EXTENSIONS.join(',')}
            />
            {showButtons && (
              <>
                <Button onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload File
                </Button>
                <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
                  <DialogTrigger asChild>
                    <Button>
                      <FolderPlus className="h-4 w-4 mr-2" />
                      New Folder
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Folder</DialogTitle>
                  <DialogDescription>
                    Enter a name for the new folder in {currentPath || 'My Drive'}
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Input
                    placeholder="Folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateFolder()
                      }
                    }}
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowNewFolderDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateFolder}
                    disabled={createFolderMutation.isPending || !newFolderName.trim()}
                  >
                    {createFolderMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Input
          placeholder="Search files and folders..."
          className="max-w-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <FileBrowser
          items={filteredData}
          isLoading={isLoading}
          view={view}
          currentPath={currentPath}
          onPathChange={setCurrentPath}
          onViewChange={setView}
          onItemClick={handleItemClick}
          onMove={handleMove}
          onGetDetails={handleGetDetails}
          onCreateFolder={() => setShowNewFolderDialog(true)}
        />
      )}

      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedItem?.name}</DialogTitle>
            <DialogDescription>
              {!selectedItem?.size ? 'Folder' : 'File'} Details
            </DialogDescription>
          </DialogHeader>
          {isLoadingDetails ? (
            <div className="flex items-center justify-center h-24">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : itemDetails?.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-muted-foreground">Type</div>
                <div>{!selectedItem?.size ? 'Folder' : 'File'}</div>
                
                <div className="text-muted-foreground">Size</div>
                <div>{formatSize(selectedItem?.size)}</div>
                
                <div className="text-muted-foreground">Created</div>
                <div>{selectedItem?.created_at ? 
                  format(new Date(selectedItem.created_at), 'PPpp') : '-'}</div>
                
                <div className="text-muted-foreground">Modified</div>
                <div>{selectedItem?.updated_at ? 
                  format(new Date(selectedItem.updated_at), 'PPpp') : '-'}</div>

                {selectedItem?.url && (
                  <>
                    <div className="text-muted-foreground col-span-2 pt-4 pb-2">Public URL</div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Input
                            value={selectedItem.url}
                            readOnly
                            className="pr-20 font-mono text-sm"
                            onClick={(e) => e.currentTarget.select()}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="absolute right-0 top-0 h-full px-3 hover:bg-accent"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedItem.url || '')
                              toast.success("URL copied to clipboard")
                            }}
                          >
                            Copy URL
                          </Button>
                        </div>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() => window.open(selectedItem.url, '_blank')}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-4 w-4"
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Anyone with this URL can access the file
                      </p>
                    </div>
                  </>
                )}
              </div>

              {selectedItem?.size && selectedItem?.url && (
                <div className="pt-4">
                  <Button
                    className="w-full"
                    onClick={() => window.open(selectedItem.url, '_blank')}
                  >
                    Open File
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <LoadingDialog
        open={!!uploadProgress}
        onOpenChange={() => setUploadProgress(null)}
        title="Uploading File"
        description={uploadProgress || ""}
      />
    </div>
  )
}
